import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import CurrencyInput from 'react-currency-input-field';
import { IoIosArrowBack } from 'react-icons/io';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { Input, InputRadio, Select } from '~/components';
import { useAuth } from '~/hooks/auth';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';

import * as S from '../styles';

import { Package } from '..';

const StatusOptionsArr = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
];
type LealtadOptions = 'monthly' | 'quarterly' | 'semiannual' | 'yearly';

type SelectOption = {
  value: number;
  label: string;
};

type FormProps = {
  name: string;
  value: string;
  number_of_training_programs: string;
  status: SelectOption;
  lealtad?: LealtadOptions;
  total_amount?: number;
};

type Props = {
  isCreateOrEdit: 'create' | 'edit';
  editPackageData: Package;
  showContent: React.Dispatch<
    React.SetStateAction<'packages' | 'create-or-edit-package'>
  >;
  loadPackages: () => Promise<void>;
};

const inputStyle = {
  select: {
    placeholder: () => ({
      padding: '0 1rem',
      color: '#00000080',
    }),
    singleValue: () => ({
      padding: '0 1rem',
    }),
  },
};

const monthlyValues = [
  { value: 'monthly', label: 'Mensal' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'semiannual', label: 'Semestral' },
  { value: 'yearly', label: 'Anual' },
];

const CreateOrEditPackage = ({
  isCreateOrEdit,
  editPackageData,
  showContent,
  loadPackages,
}: Props) => {
  const formRef = useRef<FormHandles>(null);

  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState('0');
  const [selectedOption, setSelectedOption] =
    useState<LealtadOptions>('monthly');
  const [pageTitle] = useState(() => {
    return isCreateOrEdit === 'create' ? 'NOVO' : 'EDITAR';
  });

  const statusMemo = useMemo(
    () =>
      StatusOptionsArr.map((subscription) => ({
        value: subscription.value,
        label: subscription.label,
      })),
    [StatusOptionsArr],
  );

  const handleOptionChange = useCallback(
    (option: LealtadOptions) => {
      if (isCreateOrEdit === 'create') {
        setSelectedOption(option);
      }
    },
    [isCreateOrEdit],
  );

  const handleSubmit = useCallback(
    async (data: FormProps) => {
      try {
        const schema = Yup.object().shape({
          professional_id: Yup.number().required(),
          name: Yup.string().required('O campo é obrigatório!'),
          value: Yup.number().required('O campo é obrigatório!'),
          number_of_training_programs: Yup.number().required(
            'O campo é obrigatório!',
          ),
          lealtad: Yup.string().required('Opção obrigatória!'),
          status: Yup.string().required('O campo é obrigatório!'),
        });

        const dataCopy = { ...data };

        delete dataCopy.total_amount;

        const formData = {
          ...dataCopy,
          value: String(amount),
          lealtad: selectedOption,
          professional_id: user.professional.id,
          gym_id: null,
        };

        await schema.validate(formData, { abortEarly: false });

        if (isCreateOrEdit === 'edit') {
          await api.put(`/packages/${editPackageData.id}`, formData);
          toast.success('Pacote atualizado com sucesso!');
        } else {
          await api.post('/packages', formData);
          toast.success('Pacote criado com sucesso!');
        }

        await loadPackages();
        showContent('packages');
      } catch (err) {
        console.log(err);
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        }

        toast.error('Erro ao cadastrar pacote!');
      }
    },
    [amount, selectedOption, user.professional.id],
  );

  const handleRenderCheckbox = useCallback(() => {
    if (isCreateOrEdit === 'create') {
      return monthlyValues.map((data) => (
        <S.RadioContainer>
          <InputRadio
            name={data.label}
            value={data.label}
            checked={selectedOption === data.value}
            onChange={() => handleOptionChange(data.value as LealtadOptions)}
          />
          {data.label}
        </S.RadioContainer>
      ));
    }

    return monthlyValues
      .filter((data) => data.value === selectedOption)
      .map((data) => (
        <S.RadioContainer>
          <InputRadio
            name={data.label}
            value={data.label}
            checked={selectedOption === data.value}
            onChange={() => handleOptionChange(data.value as LealtadOptions)}
          />
          {data.label}
        </S.RadioContainer>
      ));
  }, [handleOptionChange, selectedOption]);

  useEffect(() => {
    const lealtedTime = {
      monthly: 1,
      quarterly: 3,
      semiannual: 6,
      yearly: 12,
    };

    setTotalAmount((amount * lealtedTime[selectedOption]).toFixed(2));
  }, [selectedOption, amount]);
  useEffect(() => {
    if (isCreateOrEdit === 'edit') {
      setSelectedOption(editPackageData.lealtad as LealtadOptions);
      setAmount(editPackageData.value);
    }
  }, [editPackageData, isCreateOrEdit]);

  return (
    <>
      <S.Header>
        <S.PageHeaderBackButton onClick={() => showContent('packages')}>
          <IoIosArrowBack />
          <S.PageHeaderBackButtonLabel>
            {pageTitle} PACOTE
          </S.PageHeaderBackButtonLabel>
        </S.PageHeaderBackButton>

        <S.PageHeaderSaveButton
          disabled={loading}
          onClick={() => formRef.current?.submitForm()}
        >
          Salvar
        </S.PageHeaderSaveButton>
      </S.Header>

      <S.Container>
        <Form ref={formRef} onSubmit={handleSubmit} id="package-form">
          <div>
            <S.FieldSet>
              <S.FormLabel>Nome: </S.FormLabel>
              <Input
                disabled={isCreateOrEdit === 'edit'}
                name="name"
                type="text"
                defaultValue={
                  isCreateOrEdit === 'edit' ? editPackageData.name : ''
                }
                containerStyle={inputStyle}
                fontSize="1.5rem"
              />
            </S.FieldSet>

            <S.FieldSet>
              <S.FormLabel>N° de Programas de treino: </S.FormLabel>
              <Input
                disabled={isCreateOrEdit === 'edit'}
                name="number_of_training_programs"
                type="number"
                defaultValue={
                  isCreateOrEdit === 'edit'
                    ? editPackageData.number_of_training_programs
                    : 0
                }
                containerStyle={inputStyle}
                fontSize="1.5rem"
              />
            </S.FieldSet>

            <S.FieldSet>
              <S.SelectContainer>
                <S.FormLabel>Status: </S.FormLabel>
                <Select
                  name="status"
                  isSearchable={false}
                  placeholder="Selecionar"
                  defaultValue={
                    isCreateOrEdit === 'edit'
                      ? StatusOptionsArr.find(
                          (opt) => opt.value === editPackageData.status,
                        )
                      : null
                  }
                  options={statusMemo}
                  styles={inputStyle.select}
                />
              </S.SelectContainer>
            </S.FieldSet>

            <S.FieldSet>
              <S.FormLabel>Valor: </S.FormLabel>
              <CurrencyInput
                disabled={isCreateOrEdit === 'edit'}
                id="input-currency"
                name="value"
                placeholder="R$ 100"
                defaultValue={
                  isCreateOrEdit === 'edit' ? editPackageData.value : amount
                }
                decimalsLimit={2}
                prefix="R$ "
                onValueChange={(value) => {
                  setAmount(Number(value?.replace(',', '.')));
                }}
              />
            </S.FieldSet>

            <S.FieldSet>
              <S.FormLabel>Valor total: </S.FormLabel>
              <CurrencyInput
                disabled
                id="input-currency"
                name="total_amount"
                value={totalAmount || 0}
                decimalsLimit={2}
                prefix="R$ "
                onValueChange={(value) => setAmount(Number(value))}
              />
            </S.FieldSet>
          </div>

          <S.FieldSet id="fidelization">
            <S.FormLabel>Fidelização:</S.FormLabel>
            {handleRenderCheckbox()}
          </S.FieldSet>
        </Form>
      </S.Container>
    </>
  );
};

export default CreateOrEditPackage;
